/**
 * Admin Dispute Queue (`/admin/disputes`).
 *
 * Lists every dispute that still needs a human decision (open / evidence /
 * review). Replaces the CLI workflow `pnpm tsx scripts/resolve-dispute.ts`
 * — the operator can now triage and resolve disputes from the Mini App.
 *
 * Role gating happens at three layers (defense in depth):
 *   1. Backend `disputeController.requireArbiter` (canonical, 401 on miss).
 *   2. This page's `useEffect` redirects non-admins to /deals before fetch.
 *   3. Failed fetch with `forbidden` ApiError is rendered as a hard "no
 *      access" card so even if a future schema drift slips a non-admin
 *      through, they hit a clear stop sign instead of an empty queue.
 *
 * The list is fetched once on mount; manual refresh reloads. There is no
 * automatic polling — disputes are not high frequency and BullMQ-driven
 * sweeps are server-side. We avoid setInterval to keep the page idle when
 * the operator is reviewing a single dispute.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle, RefreshCw, ShieldAlert } from 'lucide-react';

import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { EmptyState } from '@/components/common/EmptyState';
import { disputes as disputesApi, isApiError } from '@/lib/api';
import type { AdminDisputeRow } from '@/lib/api/types';
import { useT } from '@/lib/i18n';
import { useAppStore } from '@/lib/state/store';
import { nanoToTon } from '@/lib/ton/format';
import { formatRelativeTime, truncateText } from '@/lib/utils/format';
import { haptics } from '@/lib/telegram/haptics';
import { ROUTES } from '@/routes/router';

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; rows: AdminDisputeRow[] }
  | { kind: 'forbidden' }
  | { kind: 'error'; message: string };

/** Resolve a party display label without ever showing a raw UUID. */
function partyLabel(p: AdminDisputeRow['buyer'] | AdminDisputeRow['seller']): string {
  if (!p) return '—';
  if (p.username) return `@${p.username}`;
  if (p.firstName) return p.firstName;
  return `tg:${p.telegramId}`;
}

export function AdminDisputeQueuePage(): JSX.Element {
  const navigate = useNavigate();
  const t = useT();
  const userSnapshot = useAppStore((state) => state.user);
  const [state, setState] = useState<LoadState>({ kind: 'loading' });

  // Frontend role gate — kicks the user out before the fetch fires. The
  // backend would 401 anyway, but bouncing here keeps the queue invisible
  // to non-admins entirely.
  useEffect(() => {
    if (!userSnapshot) return; // wait for snapshot to hydrate
    if (userSnapshot.role !== 'admin' && userSnapshot.role !== 'arbiter') {
      navigate(ROUTES.deals, { replace: true });
    }
  }, [userSnapshot, navigate]);

  const load = useCallback(async () => {
    setState({ kind: 'loading' });
    try {
      const response = await disputesApi.listAdminQueue();
      setState({ kind: 'ready', rows: response.disputes });
    } catch (err) {
      if (isApiError(err) && (err.is('unauthorized_actor') || err.status === 401)) {
        setState({ kind: 'forbidden' });
        return;
      }
      const message = err instanceof Error ? err.message : t('admin.queue.loadError');
      setState({ kind: 'error', message });
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const handleRefresh = useCallback(() => {
    haptics.light();
    void load();
  }, [load]);

  const handleOpen = useCallback(
    (disputeId: string) => {
      haptics.medium();
      navigate(`/admin/disputes/${disputeId}`);
    },
    [navigate],
  );

  const renderBody = useMemo(() => {
    if (state.kind === 'loading') {
      return (
        <div className="flex flex-col gap-3">
          {[0, 1, 2].map((i) => (
            <Card key={i} className="flex flex-col gap-2">
              <Skeleton className="h-4 w-1/3" />
              <Skeleton className="h-3 w-2/3" />
              <Skeleton className="h-3 w-1/2" />
            </Card>
          ))}
        </div>
      );
    }

    if (state.kind === 'forbidden') {
      return (
        <Card className="flex items-start gap-3 border-danger/40 bg-danger/10">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden />
          <p className="text-sm text-text-secondary">{t('admin.queue.unauthorized')}</p>
        </Card>
      );
    }

    if (state.kind === 'error') {
      return (
        <Card className="flex flex-col items-start gap-3 border-danger/40 bg-danger/10">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 h-5 w-5 shrink-0 text-danger" aria-hidden />
            <p className="text-sm text-text-secondary">{state.message}</p>
          </div>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            {t('common.retry')}
          </Button>
        </Card>
      );
    }

    if (state.rows.length === 0) {
      return (
        <EmptyState
          icon={<ShieldAlert className="h-6 w-6 text-accent-gold" aria-hidden />}
          title={t('admin.queue.empty.title')}
          description={t('admin.queue.empty.body')}
        />
      );
    }

    return (
      <div className="flex flex-col gap-3">
        {state.rows.map(({ dispute, deal, buyer, seller, filedBy }) => {
          const amountTon = nanoToTon(BigInt(deal.amount), 4);
          return (
            <Card
              key={dispute.id}
              className="flex flex-col gap-3 transition hover:border-accent-purple/40"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-semibold text-text-primary">
                    {truncateText(deal.title, 48)}
                  </p>
                  <p className="mt-0.5 text-xs text-text-muted">#{deal.shortId}</p>
                </div>
                <Badge variant="danger">{dispute.status}</Badge>
              </div>

              <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs">
                <div>
                  <span className="text-text-muted">{t('admin.queue.amount')}</span>
                  <p className="font-medium text-text-primary">{amountTon} TON</p>
                </div>
                <div>
                  <span className="text-text-muted">{t('admin.queue.filed')}</span>
                  <p className="font-medium text-text-primary">
                    {formatRelativeTime(dispute.createdAt)}
                  </p>
                </div>
                <div>
                  <span className="text-text-muted">{t('admin.queue.buyer')}</span>
                  <p className="truncate font-medium text-text-primary">
                    {partyLabel(buyer)}
                  </p>
                </div>
                <div>
                  <span className="text-text-muted">{t('admin.queue.seller')}</span>
                  <p className="truncate font-medium text-text-primary">
                    {partyLabel(seller)}
                  </p>
                </div>
              </div>

              <div className="rounded-md border border-border/40 bg-bg-tertiary/40 p-2.5">
                <p className="text-[10px] uppercase tracking-wider text-text-muted">
                  {t('admin.queue.filedBy')} {partyLabel(filedBy)}
                </p>
                <p className="mt-1 text-xs text-text-secondary">
                  {truncateText(dispute.reason, 140)}
                </p>
              </div>

              <Button
                variant="default"
                size="sm"
                onClick={() => handleOpen(dispute.id)}
              >
                <ShieldAlert className="h-4 w-4" aria-hidden />
                {t('admin.queue.review')}
              </Button>
            </Card>
          );
        })}
      </div>
    );
  }, [state, handleOpen, handleRefresh, t]);

  return (
    <AppShell>
      <PageHeader
        title={t('admin.queue.title')}
        description={t('admin.queue.subtitle')}
        action={
          <Button
            variant="ghost"
            size="icon"
            aria-label={t('common.retry')}
            onClick={handleRefresh}
            disabled={state.kind === 'loading'}
          >
            <RefreshCw
              className={state.kind === 'loading' ? 'h-5 w-5 animate-spin' : 'h-5 w-5'}
              aria-hidden
            />
          </Button>
        }
      />
      {renderBody}
    </AppShell>
  );
}
